import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useApp } from "../context/AppContext";
import { scopedTransactionSelect } from '../utils/secureQuery';
import { parseUuid } from '../utils/inputValidation';
import { motion, AnimatePresence } from "framer-motion";
import { 
  CheckCircle2, ArrowRight, ArrowDown, 
  Wallet, Landmark, Receipt, ChevronLeft, ChevronDown, Info 
} from "lucide-react";
import ReactCountryFlag from "react-country-flag"; // 🔥 Importado para solucionar banderas en PC

// --- Componente de Logo ---
const MethodLogo = ({ methodName, size = "small" }) => {
  const [hasError, setHasError] = useState(false);

  const formattedName = methodName?.toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, '-') || 'default';
    
  const imagePath = `/logos/${formattedName}.png`;
  const sizeClasses = size === "large" ? "w-14 h-14" : "w-10 h-10";
  const iconSize = size === "large" ? "w-6 h-6" : "w-5 h-5";

  const getFallbackIcon = () => {
    const name = methodName?.toLowerCase() || '';
    if (name.includes("transferencia") || name.includes("banco") || name.includes("pichincha")) {
      return <Landmark className={`${iconSize} text-slate-400`} />;
    }
    return <Wallet className={`${iconSize} text-slate-400`} />;
  };

  if (hasError) {
    return (
      <div className={`${sizeClasses} shrink-0 rounded-full flex items-center justify-center shadow-sm border bg-slate-50 border-slate-200`}>
        {getFallbackIcon()}
      </div>
    );
  }

  return (
    <div className={`${sizeClasses} shrink-0 rounded-full flex items-center justify-center overflow-hidden bg-white shadow-sm border border-slate-200`}>
      <img src={imagePath} alt={`Logo`} className="w-full h-full object-cover" onError={() => setHasError(true)} />
    </div>
  );
};

const countryNames = {
  CO: "Colombia",
  VE: "Venezuela",
  US: "Estados Unidos",
  PE: "Perú",
  PA: "Panamá",
  EC: "Ecuador",
};

const METHOD_NAMES = {
  pagomovil: "Pago móvil",
  bank_transfer: "Transferencia bancaria",
  western: "Western",
  zelle: "Zelle",
  Bancolombia: "Bancolombia",
  nequi: "Nequi",
};

const FIELD_LABELS = {
  holder_name: "Titular",
  document_type: "Tipo de documento",
  document_number: "Número de documento",
  account_number: "Número de cuenta",
  bank: "Banco",
  account_type: "Tipo de cuenta",
  pone: "Número de cuenta",
};

const formatCurrency = (amount, countryCode) => {
  if (!amount && amount !== 0) return "";
  switch (countryCode) {
    case "VE":
      return `Bs ${Number(amount).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "CO":
      return `Pesos ${Number(amount).toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "PE":
      return `S/ ${Number(amount).toLocaleString("es-PE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "US":
    case "PA":
      return `USD ${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    case "EC":
      return `USD ${Number(amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    default:
      return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
};

// --- Componente de Fila para el Resumen (Optimizado para textos largos en PC) ---
const SummaryRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex flex-col md:flex-row md:justify-between md:items-start py-3 border-b border-dashed border-slate-100 last:border-b-0 gap-1 md:gap-4">
      <span className="text-slate-500 text-[10px] md:text-[11px] font-black uppercase tracking-widest md:mt-0.5 shrink-0">
        {label}
      </span>
      <span className="font-black text-slate-800 text-left md:text-right text-xs md:text-sm break-words whitespace-pre-wrap max-w-full md:max-w-[70%]">
        {value}
      </span>
    </div>
  );
};

export default function ResumenFinal() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useApp();
  const { transactionId } = location.state || {};

  const [transaction, setTransaction] = useState(null);
  const [originMethod, setOriginMethod] = useState(null);
  const [destinationMethod, setDestinationMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false); 

  useEffect(() => {
    if (!transactionId || !user?.id) {
      navigate(-1);
      return;
    }

    const fetchTransaction = async () => {
      setLoading(true);

      const { data: trx, error: trxError } = await scopedTransactionSelect(
        supabase,
        transactionId,
        user.id
      ).single();

      if (trxError || !trx) {
        console.error("Error cargando transacción:", trxError);
        setLoading(false);
        return;
      }

      setTransaction(trx);

      if (trx.payment_method_origin_id) {
        const { data: origin, error: originError } = await supabase
          .from("payment_methods_origin")
          .select("*")
          .eq("id", parseUuid(trx.payment_method_origin_id, 'Método origen'))
          .single();
        if (!originError) setOriginMethod(origin);
      }

      if (trx.payment_method_destination_id) {
        const { data: dest, error: destError } = await supabase
          .from("user_payment_methods")
          .select("*")
          .eq("id", parseUuid(trx.payment_method_destination_id, 'Método destino'))
          .eq("user_id", user.id)
          .single();
        if (!destError) setDestinationMethod(dest);
      }

      setLoading(false);
    };

    fetchTransaction();
  }, [transactionId, user?.id, navigate]);

  const handleBack = () => {
    navigate(-1);
  };

  const renderFields = (data, methodName) => {
    if (!data) return null;
    return Object.entries(data).map(([key, value]) => {
      if (!value) return null;

      let label = FIELD_LABELS[key] || key.replace("_", " ").toUpperCase();

      if (key === "bank" && value === (methodName || "")) return null;
      if (key.toLowerCase() === "phone") label = "Número de cuenta";

      return <SummaryRow key={key} label={label} value={value} />;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f7fb]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
          <p className="text-blue-900/70 font-black uppercase tracking-widest text-[10px]">Generando recibo...</p>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f4f7fb] p-5">
        <div className="text-center bg-white p-6 rounded-[24px] shadow-sm border border-red-100 max-w-sm w-full">
          <p className="text-lg font-black text-slate-800 tracking-tight text-red-500">Transacción no encontrada.</p>
          <button onClick={() => navigate('/history')} className="mt-6 w-full py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-black text-xs uppercase tracking-widest rounded-xl transition-colors">Volver al inicio</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f7fb] px-4 py-4 md:py-8 relative">
      <div className="mx-auto w-full max-w-md">
        
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-5 md:mb-6">
          <div className="inline-flex items-center justify-center w-10 h-10 bg-blue-100 text-blue-600 rounded-full mb-3 shadow-sm">
            <Receipt className="w-5 h-5" />
          </div>
          <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight leading-tight">
            Resumen Final
          </h1>
          <p className="text-slate-500 text-xs font-medium mt-1">Detalles de tu operación</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="space-y-4">
          
         {/* ✅ BANNER DE ÉXITO (Restaurado a original) */}
          {transaction.destination_reference_number && (
            <div className="bg-green-500 rounded-[24px] p-5 md:p-6 shadow-lg shadow-green-500/20 text-white flex flex-col items-center text-center relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
              <CheckCircle2 className="w-10 h-10 mb-2 text-white drop-shadow-sm" />
              <h2 className="text-xl md:text-2xl font-black tracking-tight mb-1">¡Transacción Exitosa!</h2>
              <p className="text-green-50 font-medium text-xs mb-4">El dinero ha llegado a su destino.</p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3 w-full border border-white/20 shadow-inner">
                <span className="block text-[9px] font-black uppercase tracking-widest text-green-100 mb-0.5">N° de Referencia</span>
                <span className="text-lg font-mono font-black tracking-wider">{transaction.destination_reference_number}</span>
              </div>
            </div>
          )}

          {/* 💳 TARJETA INTERACTIVA DE FLUJO Y MONTOS */}
          <div className="bg-white rounded-[24px] shadow-sm border border-slate-100 overflow-hidden">
            <button 
              onClick={() => setShowDetails(!showDetails)}
              className="w-full p-5 md:p-6 text-center hover:bg-slate-50 transition-colors flex flex-col items-center relative"
            >
              {/* Contenedor responsivo: Columna en móviles, Fila en PC */}
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-2 w-full mb-3">
                
                {/* Origen */}
                <div className="flex flex-col items-center sm:items-start w-full sm:w-[45%] text-center sm:text-left">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1 flex items-center justify-center sm:justify-start gap-1">
                    Total Pagado <Info className="w-3 h-3"/>
                  </span>
                  <div className="flex items-center justify-center sm:justify-start gap-1.5 mb-1 w-full">
                    <ReactCountryFlag countryCode={transaction.origin_country} svg style={{ width: '1.2em', height: '1.2em', borderRadius: '2px' }} />
                    <span className="font-bold text-slate-600 text-[11px] truncate">{countryNames[transaction.origin_country]}</span>
                  </div>
                  <span className="text-[26px] sm:text-2xl font-black text-blue-600 tracking-tighter block truncate w-full">
                    {formatCurrency(transaction.total_to_pay, transaction.origin_country)}
                  </span>
                </div>

                {/* Flecha separadora (Cambia de dirección según la pantalla) */}
                <div className="hidden sm:flex items-center justify-center w-8 h-8 rounded-full bg-slate-50 border border-slate-100 z-10 shrink-0 shadow-sm">
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>
                <div className="flex sm:hidden items-center justify-center w-8 h-8 rounded-full bg-slate-50 border border-slate-100 z-10 shrink-0 shadow-sm my-1">
                  <ArrowDown className="w-4 h-4 text-slate-400" />
                </div>

                {/* Destino */}
                <div className="flex flex-col items-center sm:items-end w-full sm:w-[45%] text-center sm:text-right">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Recibido</span>
                  <div className="flex items-center justify-center sm:justify-end gap-1.5 mb-1 w-full">
                    <span className="font-bold text-slate-600 text-[11px] truncate">{countryNames[transaction.destination_country]}</span>
                    <ReactCountryFlag countryCode={transaction.destination_country} svg style={{ width: '1.2em', height: '1.2em', borderRadius: '2px' }} />
                  </div>
                  <span className="text-[26px] sm:text-2xl font-black text-green-500 tracking-tighter block truncate w-full">
                    {formatCurrency(transaction.received_amount, transaction.destination_country)}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-full mt-2 md:mt-0">
                <span>{showDetails ? 'Ocultar detalle' : 'Ver detalle'}</span>
                <motion.div animate={{ rotate: showDetails ? 180 : 0 }} transition={{ duration: 0.3 }}>
                  <ChevronDown className="w-3.5 h-3.5" />
                </motion.div>
              </div>
            </button>

            {/* Acordeón de Detalles */}
            <AnimatePresence>
              {showDetails && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden bg-slate-50/50"
                >
                  <div className="p-4 md:p-5 border-t border-dashed border-slate-200 space-y-2.5">
                    <div className="flex justify-between items-center bg-white px-3 py-2 rounded-lg border border-slate-100 shadow-sm">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Tasa aplicada</span>
                      <span className="font-bold text-slate-700 text-xs">{transaction.rate}</span>
                    </div>

                    <div className="flex justify-between items-center px-1">
                      <span className="text-slate-500 font-medium text-xs">Monto del envío</span>
                      <span className="font-bold text-slate-800 text-xs">
                        {formatCurrency(transaction.amount, transaction.origin_country)}
                      </span>
                    </div>

                    <div className="flex justify-between items-center px-1 pb-1">
                      <span className="text-slate-500 font-medium text-xs">Comisión de plataforma</span>
                      {Number(transaction.payment_fee) === 0 ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 bg-emerald-100/80 text-emerald-700 text-[9px] font-black uppercase tracking-widest rounded border border-emerald-200/50">
                          Gratis
                        </span>
                      ) : (
                        <span className="font-bold text-slate-500 text-xs">
                          + {formatCurrency(transaction.payment_fee, transaction.origin_country)}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* 💳 MÉTODO DE PAGO ORIGEN */}
          {originMethod && (
            <div className="bg-white rounded-[24px] p-5 md:p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-blue-50 p-1.5 rounded-lg text-blue-600"><Wallet className="w-4 h-4" /></div>
                <h3 className="font-black text-slate-800 text-[13px] uppercase tracking-widest">Método Origen</h3>
              </div>

              <div className="flex items-center justify-between gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-100 mb-4">
                <div className="flex items-center gap-3">
                  <MethodLogo methodName={METHOD_NAMES[originMethod.method] || originMethod.method} size="small" />
                  <div>
                    <p className="font-black text-slate-800 text-sm leading-tight">{METHOD_NAMES[originMethod.method] || originMethod.method}</p>
                  </div>
                </div>
                
                {/* 🔥 BOTÓN PUSH FIJO Y ROJO SI APLICA */}
                {originMethod.is_push && (
                  <span className="bg-red-500 text-white px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest shadow-sm">
                    PUSH
                  </span>
                )}
              </div>

              {/* 🔥 OCULTAMOS LAS INSTRUCCIONES Y DATOS SI ES PUSH */}
              {!originMethod.is_push && (
                <div className="bg-slate-50/50 px-4 py-1 rounded-2xl border border-slate-100/80">
                  {originMethod.instructions && <SummaryRow label="Instrucciones" value={originMethod.instructions} />}
                  {renderFields(originMethod.data, originMethod.method)}
                </div>
              )}
            </div>
          )}

          {/* 🏦 MÉTODO DE PAGO DESTINO */}
          {destinationMethod && (
            <div className="bg-white rounded-[24px] p-5 md:p-6 shadow-sm border border-slate-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="bg-green-50 p-1.5 rounded-lg text-green-600"><Landmark className="w-4 h-4" /></div>
                <h3 className="font-black text-slate-800 text-[13px] uppercase tracking-widest">Método Destino</h3>
              </div>

              <div className="flex items-center gap-3 bg-slate-50 p-3 rounded-2xl border border-slate-100 mb-4">
                <MethodLogo methodName={METHOD_NAMES[destinationMethod.method] || destinationMethod.method} size="small" />
                <div>
                  <p className="font-black text-slate-800 text-sm leading-tight">{METHOD_NAMES[destinationMethod.method] || destinationMethod.method}</p>
                </div>
              </div>

              <div className="bg-slate-50/50 px-4 py-1 rounded-2xl border border-slate-100/80">
                {destinationMethod.instructions && <SummaryRow label="Instrucciones" value={destinationMethod.instructions} />}
                {renderFields(destinationMethod.data, destinationMethod.method)}
              </div>
            </div>
          )}

        </motion.div>

        {/* Espaciador para la barra fija */}
        <div className="h-28 w-full"></div>
      </div>

      {/* 🚀 BOTONERA FIJA INFERIOR */}
      <div className="fixed bottom-0 left-0 w-full bg-white/95 backdrop-blur-xl border-t border-slate-200/50 p-3.5 sm:p-5 z-40 pb-safe shadow-[0_-5px_20px_rgba(0,0,0,0.03)]">
        <div className="max-w-md mx-auto">
          <button
            onClick={handleBack}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-2xl transition-all active:scale-95 font-black uppercase tracking-widest text-[13px] border border-slate-200/50"
          >
            <ChevronLeft className="w-5 h-5" /> Volver atrás
          </button>
        </div>
      </div>

    </div>
  );
}

